import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, message } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import {
  apiV2PipelinePressureMonitoringGetDeviceTypesWithPropertiesPost,
  apiV2PipelinePressureMonitoringGetPressureDataPointConfigPost,
  apiV2PipelinePressureMonitoringConfigPressureDataPointPost,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

interface PressureDataPointItem {
  typeId: number;
  typeName: string;
  dataPropertyId: number;
  dataPropertyName: string;
}

interface DataPointConfigProps {
  visible: boolean;
  onCancel: () => void;
  deviceIds?: number[];
}

interface FormData {
  [key: number]: number | undefined;
}

const DataPointConfig: React.FC<DataPointConfigProps> = ({ visible, onCancel, deviceIds }) => {
  const [form] = Form.useForm<FormData>();

  // 获取设备类型及属性
  const { data: deviceTypesData } = useRequest(
    () => apiV2PipelinePressureMonitoringGetDeviceTypesWithPropertiesPost({ ids: deviceIds }),
    {
      ready: visible,
      refreshDeps: [deviceIds],
    }
  );

  // 根据 secondLevelTypeId 去重
  const deduplicatedList = React.useMemo(() => {
    const list = deviceTypesData?.list || [];
    const seen = new Set<string>();
    const result = list.filter(item => {
      if (item.fullTypeName === undefined || seen.has(item.fullTypeName)) {
        return false;
      }
      seen.add(item.fullTypeName);
      return true;
    });
    return result;
  }, [deviceTypesData]);

  // 获取已配置的数据点（回填）
  const { data: configData } = useRequest(
    async () => {
      const res = await apiV2PipelinePressureMonitoringGetPressureDataPointConfigPost({});
      return res.pressureDataPoints;
    },
    {
      ready: visible,
      refreshDeps: [visible],
      onSuccess: data => {
        // 数据请求成功后回填表单
        if (data && data.length > 0) {
          const formData: FormData = {};
          data.forEach(item => {
            if (item.typeId && item.dataPropertyId) {
              formData[item.typeId] = item.dataPropertyId;
            }
          });
          form.setFieldsValue(formData);
        }
      },
    }
  );

  // 弹窗确认
  const handleOk = () => {
    form.validateFields().then(values => {
      const pressureDataPoints: PressureDataPointItem[] = [];
      Object.keys(values).forEach(typeIdStr => {
        const typeId = Number(typeIdStr);
        const dataPropertyId = values[typeId];

        if (dataPropertyId !== undefined && dataPropertyId !== null) {
          const deviceType = deduplicatedList.find(dt => dt.secondLevelTypeId === typeId);
          const dataProperty = deviceType?.dataProperties?.find(dp => dp.id === dataPropertyId);

          if (deviceType && dataProperty) {
            pressureDataPoints.push({
              typeId,
              typeName: deviceType.secondLevelTypeName || '',
              dataPropertyId,
              dataPropertyName: dataProperty.name || '',
            });
          }
        }
      });

      apiV2PipelinePressureMonitoringConfigPressureDataPointPost({
        pressureDataPoints,
      })
        .then(() => {
          message.success('保存成功');
          handleCancel();
        })
        .catch(err => {
          console.error('保存失败:', err);
          message.error('保存失败');
        });
    });
  };

  // 取消时重置表单
  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal
      open={visible}
      title="配置压力数据点"
      onCancel={handleCancel}
      onOk={handleOk}
      destroyOnClose
      contentClassName="modal-form-content"
    >
      <Form form={form}>
        {deduplicatedList.map(item => {
          const displayTypeName = item.fullTypeName;
          return (
            <Form.Item key={item.secondLevelTypeId} name={item.secondLevelTypeId} label={displayTypeName}>
              <Select
                placeholder="请选择数据属性"
                allowClear
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={(item.dataProperties || []).map(dp => ({
                  label: dp.name,
                  value: dp.id,
                }))}
              />
            </Form.Item>
          );
        })}
        {deduplicatedList.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>暂无可配置的设备类目</div>
        )}
      </Form>
    </Modal>
  );
};

export default DataPointConfig;
